import React, { useState, useEffect } from 'react';
import { Deck } from '../types';

interface ExportDecksModalProps {
  decks: Deck[];
  onClose: () => void;
  onExport: (selectedDeckIds: string[]) => void;
}

const ExportDecksModal: React.FC<ExportDecksModalProps> = ({ decks, onClose, onExport }) => {
  const [selectedDeckIds, setSelectedDeckIds] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(true);

  useEffect(() => {
    setSelectedDeckIds(decks.map(deck => deck.id));
  }, [decks]);

  const handleToggleDeck = (deckId: string) => {
    setSelectedDeckIds(prev => {
      const newSelection = prev.includes(deckId)
        ? prev.filter(id => id !== deckId)
        : [...prev, deckId];
      
      setSelectAll(newSelection.length === decks.length);
      return newSelection;
    });
  };

  const handleToggleAll = () => {
    if (selectAll) {
      setSelectedDeckIds([]);
      setSelectAll(false);
    } else {
      setSelectedDeckIds(decks.map(deck => deck.id));
      setSelectAll(true);
    }
  };

  const handleExport = () => {
    if (selectedDeckIds.length > 0) {
      onExport(selectedDeckIds);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900';
      case 'Medium': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900';
      case 'Hard': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900';
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Export Decks</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {decks.length === 0 ? (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <p className="text-gray-500 dark:text-gray-400">No decks available to export.</p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Select the decks you want to export ({selectedDeckIds.length} of {decks.length} selected)
                  </p>
                  <button
                    onClick={handleToggleAll}
                    className="btn btn-secondary text-sm"
                  >
                    {selectAll ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto mb-6">
                <div className="space-y-3">
                  {decks.map(deck => (
                    <div
                      key={deck.id}
                      className={`border rounded-lg p-4 transition-colors cursor-pointer ${
                        selectedDeckIds.includes(deck.id)
                          ? 'border-blue-300 bg-blue-50 dark:border-blue-600 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                      onClick={() => handleToggleDeck(deck.id)}
                    >
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedDeckIds.includes(deck.id)}
                          onChange={() => handleToggleDeck(deck.id)}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {deck.name}
                            </h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(deck.difficulty)}`}>
                              {deck.difficulty}
                            </span>
                          </div>
                          
                          {deck.description && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-1">
                              {deck.description}
                            </p>
                          )}
                          
                          <div className="flex items-center justify-between">
                            <div className="flex flex-wrap gap-1">
                              {deck.targetLanguages.slice(0, 3).map(language => (
                                <span
                                  key={language}
                                  className="px-1.5 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded text-xs"
                                >
                                  {language}
                                </span>
                              ))}
                              {deck.targetLanguages.length > 3 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  +{deck.targetLanguages.length - 3} more
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Modified {formatDate(deck.modifiedAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={onClose}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExport}
                  disabled={selectedDeckIds.length === 0}
                  className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Export {selectedDeckIds.length} {selectedDeckIds.length === 1 ? 'Deck' : 'Decks'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportDecksModal;